/* @jsxImportSource @emotion/react */
import React from 'react'
import { keyframes } from '@emotion/react'
import { FormattedMessage, useIntl } from 'react-intl'
import ReactTextareaAutosize from 'react-textarea-autosize'

import { IconButton, isComposingKeyDown } from '@slite/components'
import {
  ArrowUpIcon,
  ExpandTwoCornersIcon,
  type Icon,
  MinimizeIcon,
  PlusIcon,
  ReduceTwoCornersIcon,
  SliteAgentIcon,
} from '@slite/icons'

import type { AskHistoryTurn, HelpAskAnswer } from '../../data/sliteHelpCenter'
import {
  HELP_PAGE_MAX_WIDTH,
  HELP_SIDEBAR_WIDTH,
  HELP_TOC_WIDTH,
  helpMediaTocHidden,
} from '../../pages/help/helpTheme'
import { mediaQueryMedium } from '../style'
import { AskAnswer } from './AskAnswer'
import { useAskQuestionFromUrl } from './useAskQuestionFromUrl'

type AskTurnStatus = 'loading' | 'answered' | 'error'

// Three layout states, mirroring the app's agent panel — except the smallest
// state is the composer itself, not an icon bubble.
type AskMode = 'resting' | 'popover' | 'expanded'

interface AskTurn {
  id: number
  question: string
  answer: HelpAskAnswer | null
  status: AskTurnStatus
}

interface AskConversation {
  turns: AskTurn[]
  mode: AskMode
}

const IDLE_CONVERSATION: AskConversation = {
  turns: [],
  mode: 'resting',
}

const BAR_WIDTH = 600
const COMPACT_WIDTH = 380
const EXPANDED_WIDTH = 820

// Popover is a fixed panel (empty space above the input for short answers, like
// the app); expanded fills the viewport but stops clear of the fixed 64px
// navbar — 80px top gap + the 24px bottom offset below.
const POPOVER_HEIGHT = 'min(60vh, 520px)'
const EXPANDED_HEIGHT = `calc(100dvh - 116px)`

// Same spring the app's AgentPanel uses for its collapse/expand geometry.
const SPRING = 'cubic-bezier(0.32, 0.72, 0, 1)'

// The resting/minimized widget sits below the page's mobile overlays (the help
// sidebar drawer at 1050 and the marketing nav menu) so they cover it; the
// expanded answer — only reachable by interacting, when no overlay is open —
// rises above the navbar but stays under the search modal (1100).
const Z_RESTING = 998
const Z_EXPANDED = 1080

const panelSurface = {
  background: 'var(--shade-100)',
  border: '1px solid var(--shade-45)',
  borderRadius: 30,
  boxShadow: '0 16px 70px rgba(0, 0, 0, 0.10)',
} as const

const dotPulse = keyframes({
  '0%, 80%, 100%': { opacity: 0.25 },
  '40%': { opacity: 1 },
})

function HeaderIconButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: Icon
  label: string
  onClick: () => void
}) {
  return (
    <IconButton
      variant="quiet"
      size="small"
      iconSize="small"
      onClick={onClick}
      aria-label={label}
      icon={Icon}
    />
  )
}

function ThinkingDots() {
  return (
    <span className="flexbox flexbox-align-center flexbox-gap-2" aria-hidden>
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          css={{
            width: 4,
            height: 4,
            borderRadius: 2,
            background: 'var(--shade-30)',
            animation: `${dotPulse} 1.2s ease-in-out ${index * 0.16}s infinite`,
          }}
        />
      ))}
    </span>
  )
}

// True once the page footer scrolls into view, so the floating widget can step
// aside instead of hovering over it.
function useFooterInView() {
  const [inView, setInView] = React.useState(false)
  React.useEffect(() => {
    const footer = document.querySelector('footer')
    if (!footer) {
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: '0px 0px -24px 0px' }
    )
    observer.observe(footer)
    return () => observer.disconnect()
  }, [])
  return inView
}

// A floating composer docked at the bottom of the viewport that grows into a
// threaded conversation: each follow-up replays the earlier exchanges as
// context. It mirrors the app's agent panel — three states (resting composer →
// popover → expanded) sharing the same spring — except the smallest state is
// the composer itself, not an icon bubble. Clicking away only steps back to the
// resting composer; the thread survives navigation because the widget is
// mounted in the help pages' persistent +Layout.
export function AskWidget() {
  const intl = useIntl()
  const [{ turns, mode }, setConversation] = React.useState(IDLE_CONVERSATION)
  const [question, setQuestion] = React.useState('')
  const [isFocused, setIsFocused] = React.useState(false)
  const inputRef = React.useRef<HTMLTextAreaElement>(null)
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const abortRef = React.useRef<AbortController | null>(null)
  const nextIdRef = React.useRef(0)
  const atFooter = useFooterInView()

  const hasConversation = turns.length > 0
  const lastTurn = turns[turns.length - 1]
  const isBusy = lastTurn?.status === 'loading'
  const isOpen = mode !== 'resting'
  const isExpanded = mode === 'expanded'
  // Minimized while a thread exists: show its title as a handle, not an input.
  const restingWithThread = !isOpen && hasConversation
  const threadTitle = turns[0]?.question

  // Compact pill at rest; widens once engaged (focused or mid-question), and to
  // its full width whenever the thread is open.
  const widgetMaxWidth = isExpanded
    ? EXPANDED_WIDTH
    : isOpen || isFocused || question.trim() !== ''
      ? BAR_WIDTH
      : COMPACT_WIDTH
  // Fixed height once open so the box keeps its shape regardless of content —
  // resting stays auto (the composer/handle sizes itself).
  const panelHeight = isExpanded
    ? EXPANDED_HEIGHT
    : mode === 'popover'
      ? POPOVER_HEIGHT
      : undefined

  // biome-ignore lint/correctness/useExhaustiveDependencies: the thread and mode are the triggers that should re-scroll to the newest content
  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [turns, mode])

  const ask = React.useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (trimmed === '' || isBusy) {
        return
      }
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      const id = nextIdRef.current++
      const history: AskHistoryTurn[] = turns
        .filter((turn) => turn.status === 'answered' && turn.answer)
        .map((turn) => ({
          question: turn.question,
          answer: (turn.answer as HelpAskAnswer).answer,
        }))
      setQuestion('')
      setConversation((previous) => ({
        mode: previous.mode === 'resting' ? 'popover' : previous.mode,
        turns: [
          ...previous.turns,
          { id, question: trimmed, answer: null, status: 'loading' },
        ],
      }))
      try {
        const response = await fetch('/help/api/ask', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ question: trimmed, history }),
          signal: controller.signal,
        })
        if (!response.ok) {
          throw new Error(`Ask failed: ${response.status}`)
        }
        const result = (await response.json()) as HelpAskAnswer
        setConversation((previous) => ({
          ...previous,
          turns: previous.turns.map((turn) =>
            turn.id === id
              ? { ...turn, answer: result, status: 'answered' }
              : turn
          ),
        }))
      } catch {
        if (controller.signal.aborted) {
          return
        }
        setConversation((previous) => ({
          ...previous,
          turns: previous.turns.map((turn) =>
            turn.id === id ? { ...turn, status: 'error' } : turn
          ),
        }))
      }
    },
    [isBusy, turns]
  )

  const submit = React.useCallback(() => ask(question), [ask, question])

  useAskQuestionFromUrl({ setQuestion, ask })

  const setMode = React.useCallback(
    (next: AskMode) =>
      setConversation((previous) => ({ ...previous, mode: next })),
    []
  )
  const minimize = React.useCallback(() => setMode('resting'), [setMode])
  const toggleSize = React.useCallback(
    () => setMode(isExpanded ? 'popover' : 'expanded'),
    [setMode, isExpanded]
  )
  const openThread = React.useCallback(() => setMode('popover'), [setMode])

  const newChat = React.useCallback(() => {
    abortRef.current?.abort()
    setConversation(IDLE_CONVERSATION)
    setQuestion('')
    inputRef.current?.focus()
  }, [])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isComposingKeyDown(event)) return
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submit()
    } else if (event.key === 'Escape' && isOpen) {
      // Step down one level, like the app agent's Escape handling.
      setMode(isExpanded ? 'popover' : 'resting')
    }
  }

  return (
    <>
      {isOpen && (
        <div
          css={{
            position: 'fixed',
            inset: 0,
            zIndex: Z_EXPANDED - 1,
            background: 'rgba(38, 42, 48, 0.10)',
          }}
          onClick={minimize}
        />
      )}
      {/* Mirror the help frame's centered container + sidebar/TOC spacers, so
          the widget lands in the same content column and stays aligned to the
          white box as those columns drop — the layout positions it, no measuring
          (like the app's AppCardOverlaySlot sharing the card's grid area). */}
      <div
        css={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 24,
          zIndex: isOpen ? Z_EXPANDED : Z_RESTING,
          pointerEvents: 'none',
          // Slide out of the way once the footer is on screen, unless the thread
          // is open — that stays put.
          transform:
            atFooter && !isOpen ? 'translateY(calc(100% + 24px))' : 'none',
          opacity: atFooter && !isOpen ? 0 : 1,
          transition: 'transform 0.25s ease, opacity 0.25s ease',
        }}
      >
        <div
          css={{
            display: 'flex',
            maxWidth: HELP_PAGE_MAX_WIDTH,
            margin: '0 auto',
            padding: '0 24px',
            boxSizing: 'border-box',
            [mediaQueryMedium]: { padding: '0 16px' },
          }}
        >
          <div
            aria-hidden
            css={{
              width: HELP_SIDEBAR_WIDTH,
              flexShrink: 0,
              [mediaQueryMedium]: { display: 'none' },
            }}
          />
          <div
            css={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <div
              className="flexbox flexbox-column"
              onKeyDown={handleKeyDown}
              css={{
                width: '100%',
                maxWidth: widgetMaxWidth,
                margin: '0 12px',
                pointerEvents: atFooter && !isOpen ? 'none' : 'auto',
                transition: `max-width 320ms ${SPRING}`,
                [mediaQueryMedium]: { margin: 0 },
              }}
            >
              <div
                className="flexbox flexbox-column"
                css={{
                  ...panelSurface,
                  borderRadius: isOpen ? 16 : 30,
                  height: panelHeight,
                  overflow: 'hidden',
                  borderColor: 'var(--alpha-shade-40)',
                  transition: `height 320ms ${SPRING}, border-color 0.15s ease`,
                }}
              >
                {isOpen && (
                  <div className="flexbox flexbox-column flexbox-grow min-height-zero">
                    <header className="flexbox flexbox-align-center flexbox-gap-2 padding-horizontal-8 padding-top-8 padding-bottom-4">
                      <div className="flexbox-grow">
                        <HeaderIconButton
                          icon={PlusIcon}
                          label={intl.formatMessage({
                            id: 'website.helpCenter.ask.newChat',
                            defaultMessage: 'New chat',
                          })}
                          onClick={newChat}
                        />
                      </div>
                      <HeaderIconButton
                        icon={
                          isExpanded
                            ? ReduceTwoCornersIcon
                            : ExpandTwoCornersIcon
                        }
                        label={intl.formatMessage(
                          isExpanded
                            ? {
                                id: 'website.helpCenter.ask.reduce',
                                defaultMessage: 'Reduce',
                              }
                            : {
                                id: 'website.helpCenter.ask.expand',
                                defaultMessage: 'Expand',
                              }
                        )}
                        onClick={toggleSize}
                      />
                      <HeaderIconButton
                        icon={MinimizeIcon}
                        label={intl.formatMessage({
                          id: 'website.helpCenter.ask.minimize',
                          defaultMessage: 'Minimise',
                        })}
                        onClick={minimize}
                      />
                    </header>
                    <div
                      ref={scrollRef}
                      className="flexbox-grow min-height-zero"
                      css={{ overflowY: 'auto', padding: '4px 20px 16px' }}
                    >
                      {turns.map((turn, index) => (
                        <div
                          key={turn.id}
                          className={index > 0 ? 'margin-top-24' : ''}
                        >
                          <div className="flexbox flexbox-justify-end margin-bottom-16">
                            <div
                              className="radius-xlarger background-shade-60 padding-horizontal-16 padding-vertical-12 shade-00"
                              css={{
                                maxWidth: '82%',
                                fontSize: 14,
                                lineHeight: 1.5,
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {turn.question}
                            </div>
                          </div>

                          {turn.status === 'loading' && <ThinkingDots />}
                          {turn.status === 'error' && (
                            <div className="shade-20 font-size-7">
                              <FormattedMessage
                                id="website.helpCenter.ask.error"
                                defaultMessage="Something went wrong. Please try again."
                              />
                            </div>
                          )}
                          {turn.status === 'answered' && turn.answer && (
                            <AskAnswer
                              markdown={turn.answer.answer}
                              sources={turn.answer.sources}
                              onNavigate={minimize}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {restingWithThread ? (
                  <button
                    type="button"
                    onClick={openThread}
                    className="flexbox flexbox-align-center flexbox-gap-12 border-none background-transparent cursor-pointer text-align-left width-full"
                    css={{ minHeight: 60, paddingLeft: 20, paddingRight: 20 }}
                  >
                    <span
                      className="flexbox flexbox-centered flexbox-no-shrink"
                      css={{ color: 'var(--saturn-orange)' }}
                    >
                      <SliteAgentIcon size={20} />
                    </span>
                    <span
                      className="ellipsis flexbox-grow shade-05"
                      css={{ fontSize: 15 }}
                    >
                      {threadTitle}
                    </span>
                  </button>
                ) : (
                  <div
                    className="flexbox flexbox-align-center flexbox-gap-12"
                    css={{
                      minHeight: 60,
                      paddingLeft: 20,
                      paddingRight: 12,
                      borderTop: isOpen
                        ? '1px solid var(--alpha-shade-45)'
                        : 'none',
                    }}
                  >
                    <span
                      className="flexbox flexbox-centered flexbox-no-shrink"
                      css={{ color: 'var(--saturn-orange)' }}
                    >
                      <SliteAgentIcon size={20} />
                    </span>
                    <ReactTextareaAutosize
                      ref={inputRef}
                      value={question}
                      onChange={(event) => setQuestion(event.target.value)}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                      maxRows={4}
                      placeholder={intl.formatMessage(
                        hasConversation
                          ? {
                              id: 'website.helpCenter.ask.followUpPlaceholder',
                              defaultMessage: 'Ask a follow-up…',
                            }
                          : {
                              id: 'website.helpCenter.ask.placeholder2',
                              defaultMessage:
                                'Ask about Slite features and docs…',
                            }
                      )}
                      className="hard-disable-outline border-none background-transparent width-full shade-05"
                      css={{
                        resize: 'none',
                        fontSize: 15,
                        lineHeight: '24px',
                        height: 48,
                        padding: '12px 0',
                        '::placeholder': { color: 'var(--shade-20)' },
                      }}
                    />
                    <button
                      type="button"
                      onClick={submit}
                      disabled={question.trim() === '' || isBusy}
                      aria-label={intl.formatMessage({
                        id: 'website.helpCenter.ask.submit',
                        defaultMessage: 'Ask',
                      })}
                      className="flexbox flexbox-centered border-none cursor-pointer white flexbox-no-shrink"
                      css={{
                        width: 32,
                        height: 32,
                        borderRadius: 16,
                        background: 'var(--saturn-orange)',
                        transition: 'opacity 0.15s ease',
                        ':disabled': { opacity: 0.4, cursor: 'default' },
                      }}
                    >
                      <ArrowUpIcon size={18} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div
            aria-hidden
            css={{
              width: HELP_TOC_WIDTH,
              flexShrink: 0,
              [helpMediaTocHidden]: { display: 'none' },
            }}
          />
        </div>
      </div>
    </>
  )
}
